import { useTranslation } from "react-i18next";
import { LegalModal } from "./LegalModal";
import { LegalSection } from "./LegalSection";

export const GDPRStatement = () => {
  const { t } = useTranslation();

  return (
    <LegalModal title={t("legal.gdpr.title")}>
      <div className="space-y-6">
        {/* Metadata Header */}
        <div className="mb-8 p-4 bg-muted/30 rounded-lg space-y-2 text-sm">
          <p><strong>Effective Date:</strong> To be determined (Launch Date)</p>
          <p><strong>Last Updated:</strong> 14 October 2025</p>
          <p><strong>Company:</strong> Narrate BV t/a Momentum Lab</p>
          <p><strong>Data Protection Officer:</strong> <a href="mailto:dpo@momentumlab.earth" className="text-brand-teal hover:underline">dpo@momentumlab.earth</a></p>
          <p><strong>Contact:</strong> <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">legal@momentumlab.earth</a></p>
        </div>

        <LegalSection title={t("legal.gdpr.section1.title")}>
          <p>{t("legal.gdpr.section1.content")}</p>
          <p>Under the General Data Protection Regulation (GDPR), you have the following rights:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
            <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured, commonly used format</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            <li><strong>Right to Lodge a Complaint:</strong> File a complaint with a supervisory authority</li>
          </ul>
        </LegalSection>

        <LegalSection title="Lawful Basis for Processing">
          <p>We process your personal data under the following lawful bases:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Consent:</strong> You have given clear consent for us to process your data for a specific purpose (e.g., waitlist registration)</li>
            <li><strong>Contractual Necessity:</strong> Processing is necessary for a contract we have with you or to take pre-contractual steps</li>
            <li><strong>Legal Obligation:</strong> Processing is necessary to comply with the law</li>
            <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests (e.g., fraud prevention, improving services), provided your rights do not override these interests</li>
          </ul>
        </LegalSection>

        <LegalSection title={t("legal.gdpr.section2.title")}>
          <p>{t("legal.gdpr.section2.content")}</p>
          <p>To exercise any of your GDPR rights, please contact us at <a href="mailto:dpo@momentumlab.earth" className="text-brand-teal hover:underline">dpo@momentumlab.earth</a> or <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">legal@momentumlab.earth</a>.</p>
          <p>We will respond to your request within one month. If your request is complex or we receive multiple requests, we may extend this period by two additional months, and we will inform you of the extension.</p>
        </LegalSection>

        <LegalSection title="Data Transfer Mechanisms">
          <p>When we transfer personal data outside the European Economic Area (EEA), we ensure appropriate safeguards are in place:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Standard Contractual Clauses (SCCs):</strong> Approved by the European Commission</li>
            <li><strong>Adequacy Decisions:</strong> Transfers to countries deemed to have adequate data protection</li>
            <li><strong>Binding Corporate Rules:</strong> For transfers within multinational organisations</li>
            <li><strong>Vendor Compliance:</strong> Ensuring third-party processors comply with GDPR</li>
          </ul>
        </LegalSection>

        <LegalSection title="Data Retention Schedule">
          <p>We retain personal data only as long as necessary for the purposes outlined in our Privacy Policy:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Waitlist Data:</strong> Until platform launch or deletion request (whichever comes first)</li>
            <li><strong>User Account Data:</strong> Duration of active account plus 3 months after account closure</li>
            <li><strong>Analytics Data:</strong> Anonymised after 24 months</li>
            <li><strong>Marketing Communications:</strong> Until unsubscribe or 5 years of inactivity</li>
            <li><strong>Legal Records:</strong> 7 years for compliance with tax and legal obligations</li>
            <li><strong>Support Communications:</strong> 3 years from last interaction</li>
          </ul>
        </LegalSection>

        <LegalSection title={t("legal.gdpr.section3.title")}>
          <p>{t("legal.gdpr.section3.content")}</p>
          <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and penetration testing</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Employee training on data protection</li>
            <li>Incident response and breach notification procedures</li>
            <li>Regular backups and disaster recovery plans</li>
          </ul>
        </LegalSection>

        <LegalSection title="Automated Decision-Making and Profiling">
          <p>We do not currently engage in automated decision-making or profiling that produces legal effects or similarly significantly affects you.</p>
          <p>If we introduce such processing in the future, we will:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide clear information about the logic involved</li>
            <li>Explain the significance and consequences</li>
            <li>Obtain your explicit consent where required</li>
            <li>Provide the right to human intervention and contestation</li>
          </ul>
        </LegalSection>

        <LegalSection title={t("legal.gdpr.section4.title")}>
          <p>{t("legal.gdpr.section4.content")}</p>
          <p>If you believe we have not handled your data appropriately, you have the right to lodge a complaint with a supervisory authority, particularly in your country of residence, workplace, or where the alleged infringement occurred.</p>
          <p><strong>Netherlands Supervisory Authority:</strong></p>
          <p>
            Autoriteit Persoonsgegevens (AP)<br />
            Postbus 93374<br />
            2509 AJ Den Haag<br />
            Netherlands<br />
            Website: <a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-brand-teal hover:underline">autoriteitpersoonsgegevens.nl</a>
          </p>
          <p className="mt-3">You can also find your local supervisory authority at: <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer" className="text-brand-teal hover:underline">EDPB Members</a></p>
        </LegalSection>

        <LegalSection title="Data Protection Officer">
          <p>We have appointed a Data Protection Officer (DPO) to oversee our compliance with data protection laws. You can contact our DPO regarding any questions or concerns about our data processing activities:</p>
          <p>
            Email: <a href="mailto:dpo@momentumlab.earth" className="text-brand-teal hover:underline">dpo@momentumlab.earth</a><br />
            Postal: Data Protection Officer, Narrate BV, [Address TBD]
          </p>
        </LegalSection>

        <LegalSection title="Updates to This Statement">
          <p>We may update this GDPR Statement to reflect changes in our practices or legal requirements. Material changes will be communicated via email or a prominent notice on our platform.</p>
          <p>We encourage you to review this statement periodically to stay informed about how we protect your data.</p>
        </LegalSection>

        {/* Contact Footer */}
        <div className="mt-8 pt-6 border-t border-border text-center">
          <p className="text-foreground/70 tracking-wide">
            Questions about your data rights? Contact our DPO at{' '}
            <a href="mailto:dpo@momentumlab.earth" className="text-brand-teal hover:underline">
              dpo@momentumlab.earth
            </a>
            {' '}or{' '}
            <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">
              legal@momentumlab.earth
            </a>
          </p>
        </div>
      </div>
    </LegalModal>
  );
};
